/**
 * The realtime-gw wire protocol.
 *
 * Mirrors services/realtime-gw/wire/wire.go, which pins every one of these
 * shapes with a golden-JSON test. If a rename breaks something here, that Go
 * test should have broken first.
 */

export const PROTOCOL = "savvy.rt.v1";
export const TICKET_PREFIX = "savvy.ticket.";

export type Stream = "calls" | "queues" | "agents";

// ── envelope ──────────────────────────────────────────────────────────────

export interface Envelope<P = unknown> {
  event_id: string;
  /** "<domain>.<verb>", e.g. "call.created" or "queue.agent-offering". */
  event_type: string;
  timestamp: string;
  schema_version?: number;
  tenant?: string;
  source?: string;
  payload: P;
}

/**
 * "transferred" is the one type FreeSWITCH does not emit: callctl publishes it
 * when an attended Complete falls back to bridging on our switch (the carrier
 * declined the REFER release), so the call reads as "handed off" rather than
 * an agent still talking. A released call just ends.
 */
export type CallType =
  | "created"
  | "answered"
  | "bridged"
  | "ended"
  | "transferred"
  | "other";

export interface CallEvent {
  call_uuid: string;
  type: CallType;
  /**
   * The CHANNEL's direction. The legs the platform originates — ringing an
   * agent, a monitor leg, a consult leg — are all "outbound"; only the real
   * caller arrives "inbound". Queue counts key on this: without it the agent
   * leg reads as a waiting inbound call whose wait climbs forever.
   */
  direction?: string;
  from?: string;
  to?: string;
  hangup_cause?: string;
  /** Client attribution (BACKLOG §12): absent on platform-originated legs;
   * did present but client_* absent = the DID was unmapped. */
  did?: string;
  client_id?: string;
  client_name?: string;
}

export interface QueueEvent {
  action: string;
  queue?: string;
  agent?: string;
  agent_state?: string;
  call_uuid?: string;
  caller_number?: string;
}

export interface AgentEvent {
  extension: string;
  status: string;
  state?: string;
  changed_by?: string;
}

// ── server → client ───────────────────────────────────────────────────────

export interface HelloFrame {
  type: "hello";
  protocol: string;
  connection_id: string;
  tenant: string;
  server_time: string;
  ping_interval_ms: number;
  caps: string[];
}

export interface SnapshotCall {
  call_uuid: string;
  type: string;
  from?: string;
  to?: string;
  direction?: string;
  /**
   * FreeSWITCH's raw callstate. Exposed deliberately: mod_callcenter answers a
   * queued caller immediately for hold audio, so ACTIVE does not mean an agent
   * has them.
   */
  fs_callstate?: string;
}

export interface SnapshotAgent {
  id?: string;
  extension: string;
  display_name?: string;
  provisioned: boolean;
  status?: string;
  state?: string;
  no_answer_count: number;
  max_no_answer: number;
  calls_answered: number;
  talk_time: number;
}

export interface SnapshotFrame {
  type: "snapshot";
  seq: number;
  as_of: string;
  /** Sources that could not be read. Render these sections as UNKNOWN, not empty. */
  partial?: string[];
  data: { calls: SnapshotCall[]; agents: SnapshotAgent[] };
}

export interface EventFrame {
  type: "event";
  seq: number;
  envelope: Envelope;
}

export interface AckFrame {
  type: "ack";
  id?: string;
  streams: Stream[];
  queues?: string[];
  agents?: string[];
}

export interface PongFrame {
  type: "pong";
  id?: string;
  server_time: string;
}

export interface ErrorFrame {
  type: "error";
  id?: string;
  code: string;
  message: string;
}

export interface ShutdownFrame {
  type: "shutdown";
  reason: string;
  reconnect_after_ms: number;
}

export type ServerFrame =
  | HelloFrame
  | SnapshotFrame
  | EventFrame
  | AckFrame
  | PongFrame
  | ErrorFrame
  | ShutdownFrame;

// ── client → server ───────────────────────────────────────────────────────

export interface ClientFrame {
  type: "subscribe" | "unsubscribe" | "ping";
  id?: string;
  /** On subscribe this REPLACES the subscription; it does not add to it. */
  streams?: Stream[];
  queues?: string[];
  agents?: string[];
}

// ── close codes ───────────────────────────────────────────────────────────

export const CLOSE_GOING_AWAY = 1001;
export const CLOSE_POLICY = 1008;
export const CLOSE_TICKET = 4001;
export const CLOSE_SLOW_CONSUMER = 4002;
export const CLOSE_EVENT_GAP = 4004;

export type ConnectionStatus =
  | "connecting"
  | "live"
  | "reconnecting"
  | "stale"
  | "forbidden";
